import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ozonImage from '~/assets/img/ozon.png';
import { company } from '~/company/Company';
import { HeaderAddressWrapper } from '~/components/MobileHeaderCD/components/HeaderAddressWrapper';
import {
  HEADER_ORDER_STATUSES,
  HeaderOrders,
} from '~/components/MobileHeaderCD/components/HeaderOrders';

import { orderStore } from '~/stores';

import HeaderIconBanner from '../HeaderIconBanner/HeaderIconBanner';

import BannersDropdown from './components/BannersDropdown';
import { CatalogButton } from './components/CatalogButton';
import { ExpressBanner } from './components/ExpressBanner';
import { Logo } from './components/Logo';
import { SearchBar } from './components/SearchBar';
import { UserActions } from './components/UserActions';
import { DESKTOP_HEADER_ID } from './constants';
import styles from './HeaderV3.module.scss';
import { useIsTablet } from './hooks';

type HeaderV3Props = Omit<React.ComponentPropsWithRef<'header'>, 'children'>;

const HeaderV3 = React.forwardRef<HTMLHeadingElement, HeaderV3Props>(
  (props, ref) => {
    const activeOrders = orderStore.activeOrders?.filter((order) =>
      HEADER_ORDER_STATUSES.includes(order.public_status),
    );

    const isTablet = useIsTablet();
    const { t } = useTranslation();
    return (
      <header {...props} ref={ref}>
        {company.showHeaderExpressBanner && <ExpressBanner />}
        <div
          id={DESKTOP_HEADER_ID}
          className={cn(
            styles.header,
            (company.name === 'mcosmetic' ||
              company.name === 'dubaiexpresscom') &&
              'border border-stroke-tertiary border-b',
          )}
          data-company={company.name}
        >
          <div className={styles.headerContent}>
            <div className={styles.leftSection}>
              <Logo />
              <CatalogButton isTablet={isTablet} />
            </div>

            <SearchBar
              inverse={
                company.name !== 'mcosmetic' &&
                company.name !== 'ocado' &&
                company.name !== 'dubaiexpresscom'
              }
            />

            <div className={styles.rightSection}>
              {company.showHeaderOrders && (
                <HeaderOrders
                  className={styles.orders}
                  overlayClassName={styles.overlay}
                  dropdownClassName={styles.dropdownOrders}
                  showLabel={!isTablet}
                />
              )}
              {company.showHeaderBanners && (
                // TODO: add more details for banners
                <div className={styles.bannersWrapper}>
                  <BannersDropdown />
                  <HeaderIconBanner
                    imageSrc={ozonImage}
                    imageAlt="Ozon"
                    title={t('phrases:work')}
                    subtitle={`1 ${t('translation:year')}`}
                    variant="gray"
                  />
                </div>
              )}
              {company.showHeaderAddress && !activeOrders.length && (
                <HeaderAddressWrapper
                  className={styles.address}
                  shortTitle={isTablet}
                />
              )}
              {company.showHeaderUserActions && <UserActions />}
            </div>
          </div>
        </div>
      </header>
    );
  },
);
HeaderV3.displayName = 'HeaderV3';

export default observer(HeaderV3);
